import { RouteManager } from '../../../RouteManager';
import type { CommandHomey, CommandItem, CommandProviderArgs } from '../command-provider.types';
import { COMMAND_SECTIONS } from '../command-types.ts';

const HOMEY_SECTION_SORT_BASE = 100;

export function getHomeysCommands(args: CommandProviderArgs): CommandItem[] {
  const { activeContext, availableHomeys, selectedHomey, i18n, webAppSettings } = args;

  if (activeContext != null) {
    return [];
  }

  if (availableHomeys.length <= 1) {
    return [];
  }

  return availableHomeys.map((homey, index) => {
    const isSelectedHomey = selectedHomey?.id === homey.id;
    const homeyTypeLabel = getHomeyTypeLabel(homey);
    const subtitle =
      isSelectedHomey === true
        ? `${i18n.messageFormatter('commandPalette.homeys.current')}${
            homeyTypeLabel ? ` • ${homeyTypeLabel}` : ''
          }`
        : homeyTypeLabel;

    return {
      key: `cmd:homey:${homey.id}`,
      kind: 'command',
      section: COMMAND_SECTIONS.HOMEYS,
      sortOrder: HOMEY_SECTION_SORT_BASE + index,
      title: homey.name?.trim() || i18n.messageFormatter('commandPalette.homeys.unnamed'),
      subtitle,
      keywords: getHomeyKeywords(homey, isSelectedHomey),
      isDisabled: isSelectedHomey,
      meta: {
        imageUrl: webAppSettings?.theme?.darkMode
          ? homey.modelImage?.dark
          : homey.modelImage?.light,
      },
      getOpenInNewTabPath() {
        return RouteManager.getPathForHomey(homey.id);
      },
      onRun() {
        if (isSelectedHomey === true) {
          return;
        }

        RouteManager.toHomey(homey.id);
      },
    };
  });
}

function getHomeyKeywords(homey: CommandHomey, isSelectedHomey: boolean): string[] {
  const keywords = ['switch homey', 'change homey', 'select homey', homey.id];

  if (typeof homey.name === 'string' && homey.name.trim() !== '') {
    keywords.push(homey.name);
  }

  if (typeof homey.platform === 'string' && homey.platform !== '') {
    keywords.push(homey.platform);
  }

  if (typeof homey.model === 'string' && homey.model !== '') {
    keywords.push(homey.model);
  }

  if (homey.platform === 'cloud') {
    keywords.push('cloud');

    if (Array.isArray(homey.bridges) && homey.bridges.length > 0) {
      keywords.push('bridge', 'homey bridge');
    }
  }

  if (isSelectedHomey === true) {
    keywords.push('current');
  }

  return keywords;
}

function getHomeyTypeLabel(homey: CommandHomey): string {
  const isCloud = homey.platform === 'cloud';

  if (isCloud === true) {
    return 'Homey Cloud';
  }

  switch (homey.model) {
    case 'homey5q':
    case 'homey7q':
      return 'Homey Pro';
    case 'homey6q':
      return 'Homey Pro mini';
    case 'shs':
      return 'Homey Self-Hosted Server';
    default:
      return 'Homey Pro';
  }
}
